import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Loader2, Phone, Mail, Globe, Linkedin, Download, UserPlus, CheckCircle2, Building2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const pub = axios.create({ baseURL: API });

export default function CardPage() {
  const { slug } = useParams();
  const [card, setCard] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ firstName: "", lastName: "", company: "", email: "", phone: "", address: "" });
  const [consent, setConsent] = useState(false);
  const [sent, setSent] = useState(false);
  const [sending, setSending] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    pub.get(`/public/card/${slug}`).then(({ data }) => setCard(data)).catch(() => setNotFound(true));
  }, [slug]);

  const track = (type) => { pub.post(`/public/card/${slug}/click`, { type }).catch(() => {}); };
  const open = (type, url) => { track(type); window.open(url, type === "linkedin" || type === "website" ? "_blank" : "_self"); };
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (!consent) return setErr("Merci de cocher le consentement.");
    if (!form.firstName.trim() && !form.lastName.trim()) return setErr("Indiquez votre nom.");
    if (!form.email.trim()) return setErr("Indiquez votre email.");
    setSending(true);
    try {
      await pub.post(`/public/capture/${slug}`, { ...form, consent });
      setSent(true);
    } catch (e2) {
      setErr(e2.response?.data?.detail || "Erreur, réessayez.");
    } finally { setSending(false); }
  };

  if (notFound) return <Center><p className="font-heading text-2xl font-extrabold text-slate-800">Carte introuvable</p></Center>;
  if (!card) return <Center><Loader2 className="w-6 h-6 animate-spin text-blue-500" /></Center>;

  const initials = (card.repName || "?").split(" ").map((s) => s[0]).slice(0, 2).join("").toUpperCase();
  const L = card.links || {};
  const B = card.branding || {};
  const accent = B.accentColor;

  return (
    <div className="min-h-screen bg-slate-50 py-8 px-4">
      <div className="max-w-md mx-auto">
        <div className="bg-white rounded-3xl border border-slate-200 shadow-lg overflow-hidden" data-testid="card-page">
          <div className="h-24 bg-gradient-to-br from-blue-600 to-blue-800 relative" style={accent ? { background: accent } : undefined}>
            {B.logoUrl && <img src={B.logoUrl} alt="logo" className="absolute top-3 left-4 h-8 object-contain" data-testid="card-brand-logo" />}
          </div>
          <div className="px-6 pb-6 -mt-12">
            {card.photoUrl ? (
              <img src={card.photoUrl} alt={card.repName} className="w-24 h-24 rounded-2xl object-cover border-4 border-white shadow-md" data-testid="card-photo" />
            ) : (
              <div className="w-24 h-24 rounded-2xl bg-blue-600 border-4 border-white shadow-md flex items-center justify-center text-white font-heading font-extrabold text-3xl">{initials}</div>
            )}
            <h1 className="font-heading text-2xl font-extrabold text-slate-900 mt-4" data-testid="card-name">{card.repName}</h1>
            {card.title && <p className="text-slate-600 font-medium">{card.title}</p>}
            {card.company && <p className="text-slate-500 text-sm flex items-center gap-1.5 mt-0.5"><Building2 className="w-3.5 h-3.5" /> {card.company}</p>}
            {card.bio && <p className="text-slate-600 text-sm mt-3 leading-relaxed">{card.bio}</p>}

            <div className="grid grid-cols-2 gap-2 mt-5">
              {L.phone && <ActionBtn icon={Phone} label="Appeler" onClick={() => open("call", `tel:${L.phone}`)} testid="card-call" />}
              {card.email && <ActionBtn icon={Mail} label="Email" onClick={() => open("email", `mailto:${card.email}`)} testid="card-email" />}
              {L.website && <ActionBtn icon={Globe} label="Site web" onClick={() => open("website", L.website.startsWith("http") ? L.website : `https://${L.website}`)} testid="card-website" />}
              {L.linkedin && <ActionBtn icon={Linkedin} label="LinkedIn" onClick={() => open("linkedin", L.linkedin.startsWith("http") ? L.linkedin : `https://${L.linkedin}`)} testid="card-linkedin" />}
            </div>

            <a href={`${API}/public/card/${slug}/vcard`} onClick={() => track("vcard")} data-testid="card-vcard-button"
               className="mt-3 flex items-center justify-center gap-2 w-full h-12 rounded-xl bg-slate-900 text-white font-semibold hover:bg-slate-800 transition-colors">
              <Download className="w-5 h-5" /> Enregistrer le contact
            </a>

            {!sent ? (
              <div className="mt-3">
                {!showForm ? (
                  <Button onClick={() => setShowForm(true)} data-testid="card-leave-info-button" variant="outline" className="w-full h-12 rounded-xl">
                    <UserPlus className="w-5 h-5 mr-2" /> Me laisser vos coordonnées
                  </Button>
                ) : (
                  <form onSubmit={submit} className="space-y-3 mt-2" data-testid="card-capture-form">
                    <div className="grid grid-cols-2 gap-2">
                      <Input placeholder="Prénom" data-testid="card-firstName" value={form.firstName} onChange={set("firstName")} />
                      <Input placeholder="Nom" data-testid="card-lastName" value={form.lastName} onChange={set("lastName")} />
                    </div>
                    <Input placeholder="Email" type="email" data-testid="card-form-email" value={form.email} onChange={set("email")} />
                    <div className="grid grid-cols-2 gap-2">
                      <Input placeholder="Entreprise" data-testid="card-form-company" value={form.company} onChange={set("company")} />
                      <Input placeholder="Téléphone" data-testid="card-form-phone" value={form.phone} onChange={set("phone")} />
                    </div>
                    <Input placeholder="Adresse" data-testid="card-form-address" value={form.address} onChange={set("address")} />
                    <label className="flex items-start gap-2 text-xs text-slate-600">
                      <input type="checkbox" data-testid="card-consent" checked={consent} onChange={(e) => setConsent(e.target.checked)} className="mt-0.5 h-4 w-4 accent-blue-600" />
                      J'accepte d'être recontacté(e) (RGPD).
                    </label>
                    {err && <p className="text-sm text-rose-600" data-testid="card-error">{err}</p>}
                    <Button type="submit" disabled={sending} data-testid="card-submit" className="w-full h-11 rounded-xl">
                      {sending ? <Loader2 className="w-5 h-5 animate-spin" /> : "Envoyer"}
                    </Button>
                  </form>
                )}
              </div>
            ) : (
              <div className="mt-4 flex items-center gap-2 text-emerald-600 bg-emerald-50 rounded-xl p-3" data-testid="card-success">
                <CheckCircle2 className="w-5 h-5" /> <span className="text-sm font-medium">Merci ! Vos coordonnées ont été transmises.</span>
              </div>
            )}
          </div>
        </div>
        {B.showBadge !== false && <p className="text-center text-xs text-slate-400 mt-5">Propulsé par Kallitag</p>}
      </div>
    </div>
  );
}

function ActionBtn({ icon: Icon, label, onClick, testid }) {
  return (
    <button onClick={onClick} data-testid={testid} className="flex items-center justify-center gap-2 h-11 rounded-xl border border-slate-200 bg-slate-50 text-slate-700 font-medium text-sm hover:border-blue-400 hover:text-blue-600 transition-colors">
      <Icon className="w-4 h-4" /> {label}
    </button>
  );
}

function Center({ children }) {
  return <div className="min-h-screen flex items-center justify-center bg-slate-50 px-6 text-center">{children}</div>;
}
